import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { LoadingState } from '@/components/ui/states';
import { Table, TableWrap, THead, TH, TR, TD } from '@/components/ui/table';
import {
  FISCAL_ROUTES,
  FISCAL_PERMISSIONS,
  FiscalRegime,
  apiMessage,
  type FiscalTaxMapping,
  type UpsertFiscalTaxMappingBody,
} from '../fiscalApi';

const EMPTY: UpsertFiscalTaxMappingBody = {
  regime: FiscalRegime.EG_ETA,
  code: '',
  label: '',
  taxType: '',
  taxSubType: '',
  rate: '',
  isDefault: false,
};

/**
 * The venue's tax buckets, in the authority's vocabulary (`docs/fiscal.md`
 * step 4). **The rate is a fraction — `0.1400`, not `14`** — same shape as
 * `CatalogItem.taxRate` so the two can be compared without a conversion that
 * could be got backwards, which the input's own hint says out loud rather than
 * trusting everyone who ever opens this form to remember it.
 */
export default function TaxMappingsSection() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const { confirm } = useConfirm();
  const [target, setTarget] = useState<FiscalTaxMapping | 'new' | null>(null);
  const [form, setForm] = useState<UpsertFiscalTaxMappingBody>(EMPTY);
  const [busy, setBusy] = useState(false);

  const mappings = useApi<unknown>(can(FISCAL_PERMISSIONS.view) ? FISCAL_ROUTES.taxMappings : null);
  const { rows } = rowsOf<FiscalTaxMapping>(mappings.data);

  useEffect(() => {
    if (!target) return;
    if (target === 'new') setForm(EMPTY);
    else
      setForm({
        regime: target.regime,
        code: target.code,
        label: target.label,
        taxType: target.taxType,
        taxSubType: target.taxSubType ?? '',
        rate: target.rate,
        isDefault: target.isDefault,
      });
  }, [target]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.put(FISCAL_ROUTES.taxMappings, { ...form, taxSubType: form.taxSubType?.trim() || undefined });
      toast.success(t('fiscal.taxMappings.saved'));
      setTarget(null);
      mappings.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('fiscal.config.saveFailed')));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: FiscalTaxMapping) => {
    const ok = await confirm({
      title: t('fiscal.taxMappings.removeTitle', { code: row.code }),
      confirmLabel: t('common.remove'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(FISCAL_ROUTES.taxMapping(row.id));
      toast.success(t('fiscal.taxMappings.removed'));
      mappings.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('fiscal.config.saveFailed')));
    }
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t('fiscal.taxMappings.title')}</h2>
        {can(FISCAL_PERMISSIONS.configure) && (
          <Button size="sm" onClick={() => setTarget('new')}>
            <Icon as={Plus} />
            {t('fiscal.taxMappings.new')}
          </Button>
        )}
      </div>

      <TableWrap>
        {mappings.loading ? (
          <LoadingState />
        ) : rows.length === 0 ? (
          <div className="grid place-items-center gap-1 py-10 text-center">
            <p className="text-sm font-medium">{t('fiscal.taxMappings.empty')}</p>
            <p className="text-sm text-ink-muted">{t('fiscal.taxMappings.emptyHint')}</p>
          </div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>{t('fiscal.taxMappings.columns.code')}</TH>
                <TH>{t('fiscal.taxMappings.columns.label')}</TH>
                <TH>{t('fiscal.taxMappings.columns.authority')}</TH>
                <TH className="text-end">{t('fiscal.taxMappings.columns.rate')}</TH>
                <TH />
                <TH />
              </TR>
            </THead>
            <tbody>
              {rows.map((row) => (
                <TR key={row.id}>
                  <TD className="font-mono text-xs">{row.code}</TD>
                  <TD>{row.label}</TD>
                  <TD className="text-xs text-ink-muted">
                    {row.taxType}
                    {row.taxSubType ? ` / ${row.taxSubType}` : ''}
                  </TD>
                  <TD className="text-end tabular-nums">{(Number(row.rate) * 100).toFixed(2)}%</TD>
                  <TD>{row.isDefault && <Badge tone="brand">{t('fiscal.taxMappings.default')}</Badge>}</TD>
                  <TD>
                    {can(FISCAL_PERMISSIONS.configure) && (
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setTarget(row)}>
                          <Icon as={Pencil} size="sm" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(row)}>
                          <Icon as={Trash2} size="sm" className="text-red-600 dark:text-red-400" />
                        </Button>
                      </div>
                    )}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </TableWrap>

      {target && (
        <Dialog
          open
          onClose={() => setTarget(null)}
          title={target === 'new' ? t('fiscal.taxMappings.new') : t('fiscal.taxMappings.edit')}
          className="max-w-md"
          footer={
            <>
              <Button variant="outline" onClick={() => setTarget(null)} disabled={busy}>
                {t('common.cancel')}
              </Button>
              <Button onClick={submit} disabled={busy || !form.code.trim() || !form.rate.trim()}>
                {t('common.save')}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <Field label={t('fiscal.taxMappings.columns.code')} required controlId="tax-code">
              <Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
            </Field>
            <Field label={t('fiscal.taxMappings.columns.label')} required controlId="tax-label">
              <Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('fiscal.taxMappings.taxType')} required controlId="tax-type">
                <Input value={form.taxType} onChange={(e) => setForm((f) => ({ ...f, taxType: e.target.value }))} />
              </Field>
              <Field label={t('fiscal.taxMappings.taxSubType')} controlId="tax-subtype">
                <Input
                  value={form.taxSubType ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, taxSubType: e.target.value }))}
                />
              </Field>
            </div>
            <Field
              label={t('fiscal.taxMappings.columns.rate')}
              required
              hint={t('fiscal.taxMappings.rateHint')}
              controlId="tax-rate"
            >
              <Input
                inputMode="decimal"
                value={form.rate}
                onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))}
                placeholder="0.1400"
              />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.isDefault}
                onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
              />
              {t('fiscal.taxMappings.setDefault')}
            </label>
          </div>
        </Dialog>
      )}
    </section>
  );
}
